import axios from 'axios'

import { ArticulationType } from '@models/editor/Articulation'
import { PartType } from '@models/editor/Part'
import { asset } from '@utils/client'
import { Modulation, ModulationType, PlaybackNote } from '@utils/nf-webaudio/sequencer.types'

import { InstrumentDescriptorType } from './instrument-descriptor'
import nfsyn from './nf-webaudio'
import { SampledInstrumentZoneType } from './sampled-instrument-zone'
import { SynthesisContextType } from './synthesis-context'

// Typescript why do you do this??
interface fn {
  (): void
}
export interface SampledInstrumentType {
  part: PartType
  descriptor: InstrumentDescriptorType
  preview: boolean
  location: string | null
  baseUri: string
  sampleRate: number | null
  synth: SynthesisContextType
  zones: SampledInstrumentZoneType[]
  loaded: boolean
  numLoaded: number
  masterTuning: number
  gain: number
  release: number
  clipAttack: number
  clipRelease: number
  instrumentName: string | null
  info: InfoType
  loadCallbacks: fn[]
  loading: boolean
  pannerNode: GainNode
  panMerger: ChannelMergerNode
  panGainL: GainNode
  panGainR: GainNode
  track: null
  mixSource: PartType
  scheduledNotesGain: GainNode
  playingNotes: Record<string, never>
  setupPanning: () => void
  isMetronome: () => boolean
  partBinding: () => void
  updateGain: () => void
  createScheduledNotesGain: () => void
  parseSpecification: (xml: string) => void
  load: (callback: () => void) => void
  isPitched: () => boolean
  getNoteZone: (pitch: number, articulation: string) => SampledInstrumentZoneType | null
  scheduleNote: (
    onset: number,
    duration: number,
    pitch: number,
    velocity: number,
    articulation: string,
    modulationsList: Modulation[] | null
  ) => GainNode | null
  createNote: (
    onset: number,
    duration: number,
    pitch: number,
    velocity: number,
    articulation: ArticulationType,
    modulationsList: Modulation[] | null,
    destNode: GainNode
  ) => PlaybackNote | null
  endNoteAt: (node: PlaybackNote, time: number) => void
}

export interface InfoType {
  chordTimeSkew: number
  supportedArticulations: []
}

// Sampled instrument representation and synthesis. An instrument can produce both pre-scheduled
// notes (call scheduleNote) and real-time notes (call noteOn and noteOff).
const SampledInstrument = function (
  this: SampledInstrumentType,
  synth: SynthesisContextType,
  descriptor: InstrumentDescriptorType,
  part: PartType,
  previewFlag = false
): void {
  this.part = part
  this.descriptor = descriptor
  this.preview = previewFlag
  this.location = asset(`instruments/${descriptor.getSpecifications()[0].location}`)
  this.baseUri = this.location.replace(/[^/]+$/, '') // strip off "specification.xml" from location to obtain base URI for audio files
  this.baseUri = this.baseUri.slice(this.baseUri.indexOf('instruments'))
  this.sampleRate = descriptor.getSpecifications()[0].sampleRate
  this.synth = synth
  this.zones = []
  this.loaded = false
  this.numLoaded = 0
  this.masterTuning = 0
  this.gain = 1
  this.release = 0
  this.clipAttack = 0.04
  this.clipRelease = 0.04
  this.instrumentName = descriptor.getName()
  this.info = {
    chordTimeSkew: 0,
    supportedArticulations: [],
  }
  this.loadCallbacks = []
  this.loading = false

  this.setupPanning()
  this.createScheduledNotesGain()

  this.partBinding()

  // Map of currently playing real-time notes. Indexed by fractional MIDI note number (allows
  // detuning; hence this is an object, not an array). The values are arrays of "notes" currently
  // playing at that pitch. Doesn't contain notes that are already in their release state (i.e.,
  // have been NoteOffed). A "note" in this case is an object containing at least a pitchSource
  // and a gainNode (both AudioNodes).
  this.playingNotes = {}
}

SampledInstrument.prototype.setupPanning = function (this: SampledInstrumentType): void {
  const destinationGainNode: GainNode = this.isMetronome()
    ? this.synth.metronomeGain
    : this.synth.destination
  this.pannerNode = this.synth.context.createGain()
  this.panMerger = this.synth.context.createChannelMerger(2)

  // mono - for Now
  this.panGainL = this.synth.context.createGain()
  this.panGainR = this.synth.context.createGain()
  this.pannerNode.connect(this.panGainL)
  this.pannerNode.connect(this.panGainR)
  this.panGainL.connect(this.panMerger, 0, 0)
  this.panGainR.connect(this.panMerger, 0, 1)
  this.panMerger.connect(destinationGainNode)

  // TODO: no audio tracks for dam assets?
  this.track = null
  this.mixSource = this.track || this.part
  this.partBinding = () => {
    this.pannerNode.gain.value = this.part.muted
      ? 0
      : nfsyn.utils.decibelsToFactor(this.mixSource.gain)
    const panAngle = ((this.mixSource.pan + 1) * Math.PI) / 4
    this.panGainL.gain.value = Math.cos(panAngle) + 1.0e-10 // force nonzero gain to work around FF bug in merger
    this.panGainR.gain.value = Math.sin(panAngle) + 1.0e-10
  }
}

// Checks if the instrument uri belongs to the metronome.
SampledInstrument.prototype.isMetronome = function (this: SampledInstrumentType): boolean {
  const uri = this.descriptor.getUri()
  return uri.indexOf('metronome') > -1
}

// Load this instrument up from its specification file, then load all of the
// referenced audio resources and issue a callback when done.
SampledInstrument.prototype.load = function (
  this: SampledInstrumentType,
  callback: () => void
): void {
  // check to see if already loaded. If so, invoke this callback now
  if (this.loaded) {
    callback()
    return
  }

  // Note the callback request.
  this.loadCallbacks.push(callback)

  // If already loading, nothing more to do right now.
  if (this.loading) {
    return
  }
  this.loading = true

  if (!this.location) return
  axios
    .get(this.location, {
      headers: { 'Content-Type': 'text/xml' },
    })
    .then(({ data }) => {
      if (!nfsyn.globalSynthesisParameters.partsXmlSpecification[this.descriptor.getUri()]) {
        nfsyn.globalSynthesisParameters.partsXmlSpecification[this.descriptor.getUri()] = data
      }

      this.parseSpecification(data)
      if (!this.isMetronome()) {
        this.updateGain()
      }

      this.zones.forEach((zone) => {
        zone.load(() => {
          if (++this.numLoaded == this.zones.length) {
            this.loaded = true

            this.loadCallbacks.forEach((cb) => cb())
            this.loadCallbacks = []
          }
        })
      })
    })
    .catch(() => {
      console.error('ERROR LOADING INSTRUMENTS')
    })
}

// Parse an instrument specification
SampledInstrument.prototype.parseSpecification = function (
  this: SampledInstrumentType,
  xml: string
) {
  const parser = new DOMParser()
  const xmlDoc = parser.parseFromString(xml, 'text/xml')
  const spec = xmlDoc.getElementsByTagName('wavInstrumentSpecification')[0]

  // Extra declration here so the debugger will recognize the variable
  const n = nfsyn
  n.utils.parseProperty(parseFloat, spec, 'tuning', this, 'masterTuning')
  n.utils.parseProperty(parseFloat, spec, 'release', this)
  n.utils.parseProperty(
    function (s) {
      return n.utils.decibelsToFactor(parseFloat(s))
    },
    spec,
    'gain',
    this
  )
  n.utils.parseProperty(parseFloat, spec, 'chordTimeSkew', this.info)
  ;[...spec.getElementsByTagName('zone')].forEach((elem: Element) => {
    let zone: SampledInstrumentZoneType = new nfsyn.SampledInstrumentZone(this)
    // TODO: segregate by articulation to make it easier to fall back to default if articulation not found
    zone.parseZone(elem)

    // If this zone matches one we already loaded before, just use the cached one. Zones aren't changed from one
    // instrument instance to another.
    const cachedZone = nfsyn.instrumentZoneCache[zone.cacheKey()]
    if (cachedZone) {
      zone = cachedZone
    } else {
      nfsyn.instrumentZoneCache[zone.cacheKey()] = zone
    }

    this.zones.push(zone)
  })
}

SampledInstrument.prototype.scheduleNote = function (
  this: SampledInstrumentType,
  onset: number, // synthesis-seconds after start for playback
  duration: number, // synthesis-seconds of note duration
  pitch: number, // fractional MIDI note number allowing detuning
  velocity: number,
  articulation: ArticulationType,
  modulationsList: Modulation[] | null
): GainNode | null {
  // Scheduled notes are fed through scheduledNotesGain; see muteScheduledNotes for how this works.
  const note = this.createNote(
    onset,
    duration,
    pitch,
    velocity,
    articulation,
    modulationsList,
    this.scheduledNotesGain
  )
  if (!note) {
    return null
  }

  return note.gainNode // TODO: better to return the note.
}

// private
SampledInstrument.prototype.endNoteAt = function (
  this: SampledInstrumentType,
  note: PlaybackNote,
  time: number
): void {
  note.pitchSource.stop(time + this.release)
  if (this.release > 0) {
    note.gainNode.gain.setTargetAtTime(0, time, this.release / -Math.log(nfsyn.MINIMUM_SIGNAL))
  }
}

// private
// Duration of -1 means that the note should be held until further notice.
SampledInstrument.prototype.createNote = function (
  this: SampledInstrumentType,
  onset: number,
  duration: number,
  pitch: number,
  velocity: number,
  articulation: ArticulationType,
  modulationsList: Modulation[] | null,
  destNode: GainNode
): PlaybackNote | null {
  // Get the zone for this note
  let zone = this.getNoteZone(pitch, articulation)
  if (!zone && articulation && articulation != ArticulationType.NORMAL) {
    // if we didn't find the articulation we wanted, try again but look for normal
    zone = this.getNoteZone(pitch, ArticulationType.NORMAL)
  }
  if (!zone) {
    console.info('No zone available for pitch/vel/articulation', pitch, velocity, articulation)
    return null
  }

  // Determine frequency shift for note
  const frequencyShift = zone.getShiftForNote(pitch)

  // Create the buffer source that will generate the looped note
  const pitchSource = this.synth.context.createBufferSource()
  if (zone.loopEnd > 0) {
    if (this.synth.supportsLooping) {
      pitchSource.loop = true
      pitchSource.loopStart = zone.loopStart / (zone.sampleRate || 44100)
      pitchSource.loopEnd = zone.loopEnd / (zone.sampleRate || 44100)
    } else {
      // Use an arbitrary long duration if it's unknown...
      if (duration == -1) {
        duration = 5
      }
      zone.ensureDuration(duration * frequencyShift)
    }
  }
  pitchSource.buffer = zone.buffer

  pitchSource.playbackRate.value = frequencyShift

  // Schedule the source node of the note
  if (this.synth.supportsStart) {
    pitchSource.start(onset, zone.start / (zone.sampleRate || 44100))
  } else {
    pitchSource.start(onset)
  }

  // Create an envelope node for the release and optional loop decay
  const gainNode = this.synth.context.createGain()
  const initialGain = nfsyn.utils.decibelsToFactor(-32 + velocity * 0.25) * zone.gain
  gainNode.gain.value = initialGain
  if (zone.loopDecay > 0) {
    gainNode.gain.setTargetAtTime(
      0,
      onset + zone.loopStart / (zone.sampleRate || 44100),
      zone.loopDecay / -Math.log(nfsyn.MINIMUM_SIGNAL)
    )
  }

  pitchSource.connect(gainNode)

  // Modulations
  if (modulationsList) {
    // Modulations can be out of order. I think maybe vibrato is always after bend/slide.
    modulationsList.sort(function (a, b) {
      return a.startTime - b.startTime
    })
    let lastPitch = pitch
    let lastFrequency = frequencyShift // Used for multiple modulations
    for (let i = 0; i < modulationsList.length; i++) {
      const modulationItem = modulationsList[i]
      if (!modulationItem.modulation) continue
      switch (modulationItem.modulation.name) {
        case ModulationType.BEND: {
          break
        }
        case ModulationType.SLIDE: {
          const newPitch = modulationItem.modulation.maxValue + pitch
          const endFreq = zone.getShiftForNote(newPitch)
          pitchSource.playbackRate.setValueAtTime(lastFrequency, modulationItem.startTime)
          pitchSource.playbackRate.exponentialRampToValueAtTime(endFreq, modulationItem.endTime)
          lastPitch = newPitch
          lastFrequency = endFreq

          break
        }
        case ModulationType.VIBRATO: {
          const modDuration = modulationItem.endTime - modulationItem.startTime
          const totalPeaks = 2 * modulationItem.modulation.frequency * modDuration // Multiplied by 2 because there are 2 peaks in each period
          const semiPeriod = 1 / (2 * modulationItem.modulation.frequency)
          let timeCounter = modulationItem.startTime

          // Max and min values
          const frequencyRange = modulationItem.modulation.maxValue / 2
          const maxValue = zone.getShiftForNote(lastPitch + frequencyRange)
          const minValue = zone.getShiftForNote(lastPitch - frequencyRange)
          let amplitudeValue = 0

          // So instead we loop to set the peaks of the vibrato.
          pitchSource.playbackRate.setValueAtTime(lastFrequency, timeCounter)
          for (let j = 0; j < totalPeaks; j++) {
            amplitudeValue = j % 2 === 0 ? maxValue : minValue
            pitchSource.playbackRate.exponentialRampToValueAtTime(amplitudeValue, timeCounter)
            timeCounter = timeCounter + semiPeriod
          }
          // Back to center pitch.
          pitchSource.playbackRate.exponentialRampToValueAtTime(lastFrequency, timeCounter)

          break
        }
      }
    }
  }

  gainNode.connect(destNode)

  const note: PlaybackNote = { pitchSource: pitchSource, gainNode: gainNode }

  if (duration != -1) {
    this.endNoteAt(note, onset + duration)
  }

  return note
}

// // Determine the correct zone to use for a given note
SampledInstrument.prototype.getNoteZone = function (
  this: SampledInstrumentType,
  pitch: number,
  articulation: string
): SampledInstrumentZoneType | null {
  for (const p of [pitch, Math.floor(pitch)]) {
    for (const zone of this.zones) {
      if (zone.match(p, articulation)) {
        return zone
      }
    }
  }
  return null
}

SampledInstrument.prototype.isPitched = function (this: SampledInstrumentType): boolean {
  return !this.descriptor.isUnpitched()
}

// All scheduled notes are directed through a special gain node, so that they can be muted all at
// once when the user presses stop. This performs that muting, by ramping down that gain node;
// then creates a new gain node so that any notes scheduled in the future will be heard. The old
// node is garbage collected when all the notes feeding it are done.
SampledInstrument.prototype.muteScheduledNotes = function () {
  const now = this.synth.context.currentTime
  const gain = this.scheduledNotesGain.gain
  gain.cancelScheduledValues(now)
  gain.setValueAtTime(1, now)
  // There appears to be a bug in Chrome's k rate audio param automation that keeps our gain nodes
  // from changing their volume too soon after they are created. Giving it a few tries seems to help.
  for (let i = 1; i < 5; i++) {
    gain.exponentialRampToValueAtTime(nfsyn.MINIMUM_SIGNAL, now + nfsyn.PLAYBACK_STOP_DECAY * i)
  }
  this.createScheduledNotesGain()
}

SampledInstrument.prototype.createScheduledNotesGain = function (
  this: SampledInstrumentType
): void {
  this.scheduledNotesGain = this.synth.context.createGain()

  // Set the gain as specified in the XML.
  // If it's the metronome, don't do that. Metronome should be played as is.
  if (!this.isMetronome()) {
    this.updateGain()
  }
  this.scheduledNotesGain.connect(this.pannerNode)
}

SampledInstrument.prototype.updateGain = function (this: SampledInstrumentType): void {
  this.scheduledNotesGain.gain.setValueAtTime(this.gain, this.synth.context.currentTime)
}

export default SampledInstrument
